<script lang="ts" setup>
import { goBack, refreshFullPageContent } from "@/NavigationHelper";
import AUApi from "@/api";
import { ref, onMounted, reactive, getCurrentInstance, VueElement, watch } from "vue";
import { begriffBezeichnung } from "@/local";

interface Props {
  backVisible?: boolean;
  saveVisible?: boolean;
  refreshVisible?: boolean;
  deleteVisible?: boolean;
  createVisible?: boolean;
  triggerNavigateBack?: boolean;
  backColor?: string;
  saveColor?: string;
  deleteColor?: string;
  refreshColor?: string;
  createColor?: string;
  backIcon?: string;
  saveIcon?: string;
  deleteIcon?: string;
  refreshIcon?: string;
  createIcon?: string;
  backText?: string;
  saveText?: string;
  deleteText?: string;
  refreshText?: string;
  createText?: string;
  ignoreFunctions?: boolean;
  showBackText?: boolean;
  showDeleteText?: boolean;
  textBtn?: boolean;
  darkBtn?: boolean;
  smallBtn?: boolean;

  backFunction?: () => void;
  saveFunction?: () => void;
  deleteFunction?: () => void;
  refreshFunction?: () => void;
  createFunction?: () => void;
}

interface UiData {
  btnBackText: string;
  btnSaveText: string;
  btnDeleteText: string;
  btnRefreshText: string;
  btnCreateText: string;
  defaultbackVisible: boolean;
  defaultsaveVisible: boolean;
  defaultrefreshVisible: boolean;
  defaultdeleteVisible: boolean;
  defaultcreateVisible: boolean;
  initalized: boolean;
  isApiBusy: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(["back", "save", "delete", "afterSave", "onNew", "refresh"]);

const uiData = reactive<UiData>({
  btnBackText: "",
  btnSaveText: "",
  btnDeleteText: "",
  btnRefreshText: "",
  btnCreateText: "",
  defaultbackVisible: props.backVisible ?? true,
  defaultsaveVisible: props.saveVisible ?? true,
  defaultrefreshVisible: props.refreshVisible ?? true,
  defaultdeleteVisible: props.deleteVisible ?? true,
  defaultcreateVisible: props.createVisible ?? true,
  initalized: false,
  isApiBusy: false,
});

// Sichtbarkeit für Save-Button reaktiv mit Prop synchronisieren
watch(
  () => props.saveVisible,
  (v) => {
    uiData.defaultsaveVisible = v ?? true;
  },
  { immediate: true }
);

// Sichtbarkeit für Delete-Button reaktiv mit Prop synchronisieren
watch(
  () => props.deleteVisible,
  (v) => {
    uiData.defaultdeleteVisible = v ?? true;
  },
  { immediate: true }
);

const callbacks: {
  backFunction?: () => void;
  saveFunction?: () => void;
  deleteFunction?: () => void;
  refreshFunction?: () => void;
  createFunction?: () => void;
} = {};
onMounted(() => {
  uiData.initalized = false;
  if (props.backText) {
    uiData.btnBackText = props.backText;
  }
  if (props.saveText) {
    uiData.btnSaveText = props.saveText;
  }
  if (props.deleteText) {
    uiData.btnDeleteText = props.deleteText;
  }
  if (props.refreshText) {
    uiData.btnRefreshText = props.refreshText;
  }
  if (props.createText) {
    uiData.btnCreateText = props.createText;
  }
  if (!uiData.btnBackText && props.showBackText) {
    uiData.btnBackText = begriffBezeichnung("Close");
  }

  // if (!uiData.btnSaveText) {
  //   uiData.btnSaveText = begriffBezeichnung("Speichern");
  // }
  if (!uiData.btnDeleteText && props.showDeleteText) {
    uiData.btnDeleteText = begriffBezeichnung("Loeschen");
  }
  if (!uiData.btnRefreshText) {
    uiData.btnRefreshText = begriffBezeichnung("Refresh");
  }
  if (!uiData.btnCreateText) {
    uiData.btnCreateText = begriffBezeichnung("neu");
  }
  uiData.initalized = true;
  checkFunctionDelegates();
});

const vueInstance = getCurrentInstance();

function checkFunctionDelegates() {
  if (props.ignoreFunctions) {
    return;
  }
  let parentComponent = vueInstance?.parent as any;
  let parentComponentVue = vueInstance?.parent;
  let i = 10; // maximal 10 Ebenen
  do {
    if (!parentComponent || i <= 0) {
      break;
    }
    i--;

    const functionCheckObj = parentComponent.setupState;

    let setSomething = false;
    if (functionCheckObj.back && typeof functionCheckObj.back === "function") {
      callbacks.backFunction = functionCheckObj.back;
      setSomething = true;
    }
    if (functionCheckObj.onSave && typeof functionCheckObj.onSave === "function") {
      // juhu save gefunden!
      callbacks.saveFunction = functionCheckObj.onSave;
      setSomething = true;
    }
    if (functionCheckObj.onCancel && typeof functionCheckObj.onCancel === "function") {
      callbacks.backFunction = functionCheckObj.onCancel;
      setSomething = true;
    }
    if (functionCheckObj.save && typeof functionCheckObj.save === "function") {
      callbacks.saveFunction = functionCheckObj.save;
      setSomething = true;
    }
    if (functionCheckObj.delete && typeof functionCheckObj.delete === "function") {
      callbacks.deleteFunction = functionCheckObj.delete;
      setSomething = true;
    }
    if (functionCheckObj.refresh && typeof functionCheckObj.refresh === "function") {
      callbacks.refreshFunction = functionCheckObj.refresh;
      setSomething = true;
    }
    if (
      functionCheckObj.reloadData &&
      typeof functionCheckObj.reloadData === "function"
    ) {
      //  refreshFunction = refreshFullPageContent();
      callbacks.refreshFunction = functionCheckObj.reloadData;
    }
    if (functionCheckObj.onNew && typeof functionCheckObj.onNew === "function") {
      callbacks.createFunction = functionCheckObj.onNew;
    }
    if (setSomething) {
      break;
    }

    parentComponentVue = parentComponentVue?.parent;
    parentComponent = parentComponentVue as any;
  } while (parentComponent);
}
if (!callbacks.refreshFunction) {
  callbacks.refreshFunction = refreshFullPageContent;
}

function onBack() {
  emit("back");
  if (callbacks.backFunction) {
    callbacks.backFunction();
  } else {
    // nicht 2x.
    if (props.triggerNavigateBack) {
      goBack();
    }
  }
}

function onSave() {
  emit("save");
  const hasExplicitSaveListener = Boolean((vueInstance?.vnode.props as any)?.onSave);
  if (callbacks.saveFunction && !hasExplicitSaveListener) {
    callbacks.saveFunction();
  }
  emit("afterSave");
}

function onDelete() {
  emit("delete");
  if (callbacks.deleteFunction) {
    callbacks.deleteFunction();
  }
}
function onRefresh() {
  emit("refresh");
  if (callbacks.refreshFunction) {
    callbacks.refreshFunction();
  }
}
function onNew() {
  emit("onNew");
  if (callbacks.createFunction) {
    callbacks.createFunction();
  }
}
</script>
<template>
  <div class="d-flex justify-end" style="gap: 8px" v-if="uiData.initalized">
    <v-btn
      variant="flat"
      :class="backColor ? '' : 'dvh-back-btn'"
      @click="onBack"
      v-if="uiData.defaultbackVisible"
      :icon="backIcon ?? 'mdi-close'"
      density="comfortable"
      :text="uiData.btnBackText"
      rounded="0"
      :style="backColor ? { backgroundColor: backColor } : {}"
    >
    </v-btn>
    <v-btn
      :dark="darkBtn"
      variant="flat"
      density="comfortable"
      :color="deleteColor ?? 'red'"
      @click="onDelete"
      v-if="uiData.defaultdeleteVisible && !uiData.btnDeleteText"
      :disabled="uiData.isApiBusy"
      :icon="deleteIcon ?? 'mdi-delete'"
      rounded="0"
    >
    </v-btn>
    <v-btn
      variant="tonal"
      density="comfortable"
      :color="deleteColor ?? 'red'"
      @click="onDelete"
      v-if="uiData.defaultdeleteVisible && uiData.btnDeleteText"
      :disabled="uiData.isApiBusy"
      :prepend-icon="deleteIcon ?? 'mdi-delete'"
      :text="uiData.btnDeleteText"
      class="px-1"
      rounded="0"
    >
    </v-btn>
    <v-btn
      variant="flat"
      density="comfortable"
      :color="saveColor ?? 'green'"
      @click="onSave"
      v-if="uiData.defaultsaveVisible && !uiData.btnSaveText"
      :disabled="uiData.isApiBusy"
      :icon="saveIcon ?? 'mdi-content-save'"
      rounded="0"
    >
    </v-btn>
    <v-btn
      variant="tonal"
      density="comfortable"
      :color="saveColor ?? 'green'"
      @click="onSave"
      v-if="uiData.defaultsaveVisible && uiData.btnSaveText"
      :disabled="uiData.isApiBusy"
      :prepend-icon="saveIcon ?? 'mdi-content-save'"
      :text="uiData.btnSaveText"
      class="px-1"
      rounded="0"
    >
    </v-btn>
    <v-btn
      variant="flat"
      density="comfortable"
      :color="refreshColor ?? 'blue'"
      @click="onRefresh"
      v-if="uiData.defaultrefreshVisible"
      :disabled="uiData.isApiBusy"
      :icon="refreshIcon ?? 'mdi-refresh'"
      :text="uiData.btnRefreshText"
      rounded="0"
    >
    </v-btn>
    <v-btn
      variant="flat"
      density="comfortable"
      :color="createColor ?? 'orange'"
      @click="onNew"
      v-if="uiData.defaultcreateVisible"
      :disabled="uiData.isApiBusy"
      :icon="createIcon ?? 'mdi-plus'"
      :text="uiData.btnCreateText"
      rounded="0"
    >
    </v-btn>
  </div>
</template>
<style scoped>
.v-btn {
  padding: 0px;
}

.v-btn:not(.v-btn--round).v-size--default {
  height: 36px;
  min-width: 64px;
  padding: 0 0 0 8px;
}
.dvh-back-btn {
  background-color: #245271 !important;
  color: white !important;
}
.dvh-back-btn .v-icon {
  color: white !important;
}
</style>
